// Recovery file generation from an original ECU/MCU dump.
//
// Two families:
//  - R8C (remap): recovery = memory image. original top-aligned in a larger blank image.
//      Verified R5F21548G (64KB→96KB, marker 0xC3) & R5F21347H (48KB→64KB) structure.
//  - RH850 / V850 (hex): recovery = the original BIN converted to Intel HEX (no remap).
//
// MCU auto-detection:
//  - exact size match → R8C remap profile.
//  - otherwise inspect the 16-byte-aligned vector table: the reset/jr instruction's 2nd byte
//    is 0x07 on RH850 and 0x02 on V850 (verified on real Honda Shindengen dumps).

export const MCU_PROFILES = [
  {
    id: "R5F21548G",
    label: "Renesas R8C · R5F21548G (64KB → 96KB)",
    mode: "remap",
    inputSize: 0x10000,
    outputSize: 0x18000,
    padValue: 0xff,
    dataOffset: 0x8000,
    patches: [{ offset: 0x0, value: 0xc3, note: "recovery marker" }],
  },
  {
    id: "R5F21347H",
    label: "Renesas R8C · R5F21347H (48KB → 64KB)",
    mode: "remap",
    inputSize: 0xc000,
    outputSize: 0x10000,
    padValue: 0xff,
    dataOffset: 0x4000,
    patches: [],
  },
  {
    id: "RH850",
    label: "Renesas RH850 (BIN → Intel HEX)",
    mode: "hex",
    vecByte: 0x07,
  },
  {
    id: "V850",
    label: "Renesas V850 (BIN → Intel HEX)",
    mode: "hex",
    vecByte: 0x02,
  },
];

export function getProfile(id) {
  return MCU_PROFILES.find((p) => p.id === id) || MCU_PROFILES[0];
}

const hx = (n, w = 0) => "0x" + n.toString(16).toUpperCase().padStart(w, "0");

// ---- Intel HEX (CRLF, UPA/Renesas style) ----
function toRecord(bytesArr) {
  let sum = 0, s = "";
  for (const b of bytesArr) { sum = (sum + b) & 0xff; s += b.toString(16).padStart(2, "0").toUpperCase(); }
  const cksum = (0x100 - sum) & 0xff;
  return ":" + s + cksum.toString(16).padStart(2, "0").toUpperCase();
}
export function binToIntelHex(bytes) {
  const lines = [];
  const recLen = 16;
  let upper = -1;
  for (let i = 0; i < bytes.length; i += recLen) {
    const curUpper = (i >>> 16) & 0xffff;
    if (curUpper !== upper) {
      upper = curUpper;
      lines.push(toRecord([0x02, 0x00, 0x00, 0x04, (upper >> 8) & 0xff, upper & 0xff]));
    }
    const len = Math.min(recLen, bytes.length - i);
    const addr = i & 0xffff;
    const rec = [len, (addr >> 8) & 0xff, addr & 0xff, 0x00];
    for (let j = 0; j < len; j++) rec.push(bytes[i + j]);
    lines.push(toRecord(rec));
  }
  lines.push(":00000001FF");
  return lines.join("\r\n") + "\r\n";
}

// Inspect the vector table to distinguish RH850 (0x07) vs V850 (0x02).
export function detectVecFamily(bytes) {
  let c02 = 0, c07 = 0;
  const limit = Math.min(bytes.length, 0x800);
  for (let i = 0; i + 2 <= limit; i += 16) {
    const a = bytes[i], b = bytes[i + 1];
    if (a === 0xff || a === 0x00) continue; // blank / unused vector slot
    if (b === 0x02) c02++;
    else if (b === 0x07) c07++;
  }
  if (c07 > c02 && c07 > 0) return { id: "RH850", c02, c07 };
  if (c02 > 0) return { id: "V850", c02, c07 };
  return { id: null, c02, c07 };
}

// Returns the auto-detected profile (or null).
export function detectProfile(bytes) {
  const bySize = MCU_PROFILES.find((p) => p.mode === "remap" && p.inputSize === bytes.length);
  if (bySize) return bySize;
  const fam = detectVecFamily(bytes);
  return fam.id ? getProfile(fam.id) : null;
}

// Returns { ok, mode, bytes?, hexText?, logs:[{level,msg}] }
export function generateRecovery(original, profile) {
  const logs = [];
  const log = (level, msg) => logs.push({ level, msg });

  log("info", `Loaded original: ${original.length} bytes (${hx(original.length)})`);
  log("info", `MCU profile: ${profile.id} — ${profile.label}`);

  if (profile.mode === "hex") {
    const fam = detectVecFamily(original);
    log("ok", `Detected Renesas ${profile.id} (vector 2nd-byte ${hx(profile.vecByte, 2)} · matches=${profile.vecByte === 0x07 ? fam.c07 : fam.c02})`);
    log("info", "Recovery mode: convert original BIN → Intel HEX (no memory remap)");
    const hexText = binToIntelHex(original);
    const records = hexText.split("\r\n").filter(Boolean).length;
    log("ok", `Converted ${original.length} bytes → Intel HEX (${records} records, CRLF / UPA-Renesas style)`);
    log("ok", "HEX ready. Download as .hex.");
    return { ok: true, mode: "hex", hexText, bytes: original, logs };
  }

  // remap mode
  if (original.length !== profile.inputSize) {
    log("error", `Size mismatch: expected ${profile.inputSize} bytes (${hx(profile.inputSize)}), got ${original.length}. Aborting.`);
    return { ok: false, logs };
  }
  const out = new Uint8Array(profile.outputSize);
  out.fill(profile.padValue);
  log("info", `Allocated ${profile.outputSize} bytes (${hx(profile.outputSize)}), filled with ${hx(profile.padValue, 2)}`);
  out.set(original, profile.dataOffset);
  log("ok", `Copied original ${hx(original.length)} bytes → recovery @${hx(profile.dataOffset)}`);
  for (const p of profile.patches) {
    const abs = profile.dataOffset + p.offset;
    const oldV = out[abs];
    out[abs] = p.value;
    log("ok", `Patch recovery[${hx(abs)}]: ${hx(oldV, 2)} → ${hx(p.value, 2)}${p.note ? ` (${p.note})` : ""}`);
  }
  let blankOk = true;
  for (let i = 0; i < profile.dataOffset; i++) { if (out[i] !== profile.padValue) { blankOk = false; break; } }
  log(blankOk ? "ok" : "error", `Verify blank region [0x0..${hx(profile.dataOffset)}): ${blankOk ? "clean ✓" : "FAILED"}`);
  log("ok", `Recovery generated: ${out.length} bytes (${hx(out.length)}). Ready to download.`);
  return { ok: true, mode: "remap", bytes: out, logs };
}
